import React, { createContext, useContext, useState, useEffect } from 'react';
import { rebrandProduct, rebrandStoredProducts } from '../utils/brandText';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = useState(false);

  useEffect(() => {
    // Check authentication status
    const userRole = localStorage.getItem('userRole');
    const isCustomer = userRole === 'customer';
    setIsCustomerLoggedIn(isCustomer);

    // Load wishlist from localStorage
    if (isCustomer) {
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        try {
          setWishlistItems(rebrandStoredProducts(JSON.parse(savedWishlist)));
        } catch (error) {
          console.error('Error parsing wishlist from localStorage:', error);
          setWishlistItems([]);
        }
      }
    }

    // Listen for authentication changes
    const handleCustomStorageChange = () => {
      const newUserRole = localStorage.getItem('userRole');
      const newIsCustomer = newUserRole === 'customer';
      setIsCustomerLoggedIn(newIsCustomer);

      if (newIsCustomer) {
        // Load wishlist when customer logs in
        const savedWishlist = localStorage.getItem('wishlist');
        if (savedWishlist) {
          try {
            setWishlistItems(rebrandStoredProducts(JSON.parse(savedWishlist)));
          } catch (error) {
            console.error('Error parsing wishlist from localStorage:', error);
            setWishlistItems([]);
          }
        }
      } else {
        // Clear wishlist when customer logs out
        setWishlistItems([]);
        localStorage.removeItem('wishlist');
      }
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  const addToWishlist = (product) => {
    if (!isCustomerLoggedIn) {
      return false;
    }

    const existingItem = wishlistItems.find(item => item.id === product.id);
    if (existingItem) {
      return false; // Already in wishlist
    }

    const newWishlist = [...wishlistItems, rebrandProduct(product)];
    setWishlistItems(newWishlist);
    localStorage.setItem('wishlist', JSON.stringify(newWishlist));
    return true;
  };

  const removeFromWishlist = (productId) => {
    const newWishlist = wishlistItems.filter(item => item.id !== productId);
    setWishlistItems(newWishlist);
    localStorage.setItem('wishlist', JSON.stringify(newWishlist));
  };

  const clearWishlist = () => {
    setWishlistItems([]);
    localStorage.removeItem('wishlist');
  };

  const isInWishlist = (productId) => {
    return wishlistItems.some(item => item.id === productId);
  };

  const value = {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    isInWishlist,
    isCustomerLoggedIn
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
